import React from 'react';
import Popup, { ToolbarItem } from 'devextreme-react/popup';

import StepForm from '../Functions/StepForm';
import StepSign from '../Functions/StepSign';
import StepSignAutorize from '../Functions/StepSignAutorize';
import StepSigned from '../Functions/StepSigned';
import Button from 'devextreme-react/button';

import { useScreenSize } from '../../../../../utils/media-query';
import { useApiInformedConsents } from '../../../../../api/api.informedConsents';
import { useAppSelector } from '../../../../../contexts/hooks';
import { newToast } from '../../../..';
import { TypeConsentStatus } from '../../../../../types/types.informedConsents';

type TemplateField = any;

type Props = {
  visible: boolean;
  onClose: () => void;
  template: any; // whole report object
  estado: number; // Roc_Estado
  onUpdateDB?: (update: boolean) => void;
  currentData?: any; // the Roc_* object when exists
};

const InformedConsentModal: React.FC<Props> = ({ visible, onClose: closeModal, template, estado: initialEstado, onUpdateDB, currentData }) => {
  const { isXSmall, isSmall } = useScreenSize();
  const idOrd = useAppSelector((x) => x.globalOrders.CurrentOrder.IdOrder);
  const [ModalTemplate, setModalTemplate] = React.useState<TemplateField[] | null>([]);
  const { fillFormat, updateFormat, signDocument, SignDocumentAutorize } = useApiInformedConsents();
  const [estado, setEstate] = React.useState<TypeConsentStatus>(initialEstado ?? TypeConsentStatus.Pending);
  const [initialValues, setInitialValues] = React.useState<any | null>(null);
  const [includeChanges, setIncludeChanges] = React.useState<boolean>(false);
  const Prm_Ord_Consentimientos_U = useAppSelector((state) => state.userInfo.Permissions?.Prm_Ord_Consentimientos_U);
  const Prm_Ord_Consentimientos_C = useAppSelector((state) => state.userInfo.Permissions?.Prm_Ord_Consentimientos_C);
  const [loadingSign, setLoadingSign] = React.useState<boolean>(false);
  React.useEffect(() => {
    let tpl: TemplateField[] | null = null;
    setEstate(initialEstado ?? 0);
    try {
      // template is the whole report object; its Cst_Plantilla contains the field definitions
      // prefer the template coming from currentData if present (it may contain the exact template used when saved)
      if (currentData?.Cst_Plantilla) {
        tpl = typeof currentData.Cst_Plantilla === 'string' ? JSON.parse(currentData.Cst_Plantilla) : currentData.Cst_Plantilla;
      } else {
        tpl = template?.Cst_Plantilla ? (typeof template.Cst_Plantilla === 'string' ? JSON.parse(template.Cst_Plantilla) : template.Cst_Plantilla) : null;
      }
    } catch (err) {
      tpl = null;
    }
    // parse currentData.Roc_Plantilla to initialValues if present
    let initVals: any = null;
    try {
      initVals = currentData?.Roc_Plantilla ? (typeof currentData.Roc_Plantilla === 'string' ? JSON.parse(currentData.Roc_Plantilla) : currentData.Roc_Plantilla) : null;
    } catch (err) {
      initVals = null;
    }
    setModalTemplate(tpl);
    setInitialValues(initVals);
  }, [template, currentData, initialEstado]);

  const onComplete = async (result: any) => {
    switch (result.step) {
      case 'form':
        // if there is currentData (existing Roc), update using updateFormat and check update permission
        const payload = {
          Roc_Id: currentData?.Roc_Id,
          IdCst: template?.Cst_Id || currentData?.Roc_Cst_Id,
          IdOrd: idOrd,
          Template: JSON.stringify(result.values),
        };
        if (currentData && currentData.Roc_Id) {
          if (!Prm_Ord_Consentimientos_U) {
            newToast('WARNING', 'No tienes permisos para actualizar consentimientos.', 5);
            return;
          }
          try {
            const res = await updateFormat(payload);
            if (res) {
              newToast('SUCCESS', 'Consentimiento actualizado.', 3);
              // keep on form state (estado 1) after update
              setEstate(1);
              setIncludeChanges(true);
              //if (onUpdateDB) onUpdateDB(true);
            }
          } catch (err) {
            console.error('Error updating format', err);
          }
        } else {
          if (!Prm_Ord_Consentimientos_C) {
            newToast('WARNING', 'No tienes permisos para crear consentimientos.', 5);
            return;
          }
          try {
            const res = await fillFormat(payload);
            if (res) {
              newToast('SUCCESS', 'Consentimiento creado.', 3);
              setEstate(1); // advance to diligenciado after creation
              setIncludeChanges(true);
              //if (onUpdateDB) onUpdateDB(true);
            }
          } catch (err) {
            console.error('Error filling format', err);
          }
        }

        break;
      case 'signed':
        // Signing the initial consent -> moves to 'firmado' state (2)
        setLoadingSign(true);
        {
          const dataToSign = {
            IdOrd: idOrd,
            IdCst: template?.Cst_Id || currentData?.Roc_Cst_Id,
            FileBase64: result.values,
          };
          try {
            const resultSign = await signDocument(dataToSign);
            if (resultSign) {
              // after signing, move to 'firmado' state
              setEstate(2);
              setIncludeChanges(true);
              //if (onUpdateDB) onUpdateDB(true);
            }
          } finally {
            setLoadingSign(false);
          }
        }

        break;
      case 'authorized':
        // Signing for authorization -> finalizes the consent (4)
        setLoadingSign(true);
        {
          const dataToSignAuth = {
            IdOrd: idOrd,
            IdCst: template?.Cst_Id || currentData?.Roc_Cst_Id,
            FileBase64: result.values,
          };
          try {
            const resultAuth = await SignDocumentAutorize(dataToSignAuth);
            if (resultAuth) {
              setEstate(3); // finalized
              setIncludeChanges(true);
              //if (onUpdateDB) onUpdateDB(true);
            }
          } finally {
            setLoadingSign(false);
          }
        }

        break;
      default:
        break;
    }
  };

  const onClose = () => {
    if (includeChanges && onUpdateDB) {
      onUpdateDB(true);
    }
    closeModal();
  };

  return (
    <Popup visible={visible} onHiding={onClose} showTitle={true} title={`Consentimiento - ${template?.Cst_Nombre || ''}`} width={720} height={'auto'} showCloseButton={true} fullScreen={isXSmall || isSmall} shading={true}>
      {(() => {
        switch (estado) {
          case TypeConsentStatus.Pending:
            if (ModalTemplate && ModalTemplate.length > 0) {
              return (
                <StepForm
                  template={ModalTemplate}
                  initialValues={initialValues}
                  readOnly={!!currentData && !Prm_Ord_Consentimientos_U}
                  onCancel={onClose}
                  onSubmit={async (values) => {
                    if (onComplete) await onComplete({ step: 'form', values });
                    // after submit, you may want to advance estado externally
                  }}
                />
              );
            }
            // no template -> immediately go to signing step
            return (
              <StepSign
                onCancel={onClose}
                onSigned={async (values) => {
                  if (onComplete) await onComplete({ step: 'signed', values });
                }}
              />
            );
          case TypeConsentStatus.Filled:
            return (
              <StepSign
                onBack={() => setEstate(0)}
                isLoading={loadingSign}
                onCancel={onClose}
                // when signing here we call the authorization flow
                onSigned={async (values) => {
                  if (onComplete) await onComplete({ step: 'signed', values });
                }}
              />
            );
          case TypeConsentStatus.SignedByPatient:
            return (
              <StepSignAutorize
                onBack={() => setEstate(1)}
                isLoading={loadingSign}
                onCancel={onClose}
                onSigned={async (values) => {
                  if (onComplete) await onComplete({ step: 'authorized', values });
                }}
              />
            );
          case TypeConsentStatus.SignedByAuthorized:
          case TypeConsentStatus.Completed:
            return <StepSigned onClose={onClose} />;
          default:
            // default: show form if template, otherwise message
            if (ModalTemplate && ModalTemplate.length > 0) {
              return <StepForm template={ModalTemplate} initialValues={initialValues} readOnly={!!currentData && !Prm_Ord_Consentimientos_U} onCancel={onClose} onSubmit={async (values) => onComplete && (await onComplete({ step: 'form', values }))} />;
            }
            break;
        }

        return <div style={{ padding: 16 }}>No hay acciones disponibles para este consentimiento.</div>;
      })()}
    </Popup>
  );
};

export default InformedConsentModal;
